<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex align-center py-3">
      <v-icon
        color="primary"
        class="mr-2"
      >
        {{ icons.mdiChartBar }}
      </v-icon>
      <span>来店パターン（直近3ヶ月）</span>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <template v-else-if="!visitPattern || totalVisits === 0">
        <div class="text-center text--secondary py-4">
          来店履歴がありません
        </div>
      </template>

      <template v-else>
        <v-row>
          <!-- 曜日別来店回数 -->
          <v-col cols="6">
            <div class="text-subtitle-2 mb-2">
              曜日別来店回数
            </div>
            <div
              v-for="day in dayOfWeekData"
              :key="day.dayOfWeek"
              class="d-flex align-center mb-1"
            >
              <div class="day-label-col text-body-2">
                {{ day.label }}
              </div>
              <v-sheet
                color="primary"
                rounded
                class="bar mr-2"
                :style="{ width: getBarWidth(day.count, maxDayCount) }"
              />
              <span class="text-caption">{{ day.count }}</span>
            </div>
          </v-col>

          <!-- 時間帯別来店回数 -->
          <v-col cols="6">
            <div class="text-subtitle-2 mb-2">
              時間帯別来店回数
            </div>
            <div
              v-for="hour in filteredHourData"
              :key="hour.hour"
              class="d-flex align-center mb-1"
            >
              <div class="hour-label-col text-body-2">
                {{ hour.label }}
              </div>
              <v-sheet
                color="info"
                rounded
                class="bar mr-2"
                :style="{ width: getBarWidth(hour.count, maxHourCount) }"
              />
              <span class="text-caption">{{ hour.count }}</span>
            </div>
          </v-col>
        </v-row>

        <!-- サマリー -->
        <v-alert
          v-if="peakDay || peakHour"
          type="info"
          text
          dense
          class="mt-4"
        >
          <span v-if="peakDay">最も来店が多い曜日: <strong>{{ peakDay.label }}（{{ peakDay.count }}回）</strong></span>
          <span v-if="peakDay && peakHour"> / </span>
          <span v-if="peakHour">最も来店が多い時間帯: <strong>{{ peakHour.label }}（{{ peakHour.count }}回）</strong></span>
        </v-alert>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, getCurrentInstance } from '@vue/composition-api'
import { mdiChartBar } from '@mdi/js'
import useCustomerVisitPattern from '../composables/useCustomerVisitPattern'
import useCurrentData from '@/views/composable/useCurrentData'

const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

// openAt/closeAtの時刻文字列から時(hour)を抽出する
// 例: "2000-01-01T18:00:00.000+09:00" → 18, "18:00" → 18
const parseHour = timeStr => {
  if (!timeStr) return null
  const match = timeStr.match(/(\d{1,2}):\d{2}/)

  return match ? parseInt(match[1], 10) : null
}

export default {
  name: 'CustomerVisitPattern',
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()

    const {
      visitPattern,
      loading,
      fetchCustomerVisitPattern,
    } = useCustomerVisitPattern(vm)

    // 店舗の営業時間からフィルター関数を生成
    const isClubHour = computed(() => {
      const openHour = parseHour(currentClub.value.openAt)
      const closeHour = parseHour(currentClub.value.closeAt)
      if (openHour === null || closeHour === null) {
        // 営業時間未設定の場合は全時間帯を表示
        return () => true
      }
      if (openHour <= closeHour) {
        // 日中営業（例: 10:00〜22:00）
        return hour => hour >= openHour && hour <= closeHour
      }

      // 深夜またぎ（例: 18:00〜05:00）
      return hour => hour >= openHour || hour <= closeHour
    })

    const isLoading = computed(() => loading.value)

    const totalVisits = computed(() => {
      if (!visitPattern.value) return 0
      const { summary } = visitPattern.value

      return summary ? summary.totalVisits || 0 : 0
    })

    const dayOfWeekData = computed(() => {
      if (!visitPattern.value || !visitPattern.value.byDayOfWeek) return []

      return visitPattern.value.byDayOfWeek.map(d => ({
        ...d,
        label: DAY_LABELS[d.dayOfWeek] || '',
      }))
    })

    const filteredHourData = computed(() => {
      if (!visitPattern.value || !visitPattern.value.byHour) return []

      return visitPattern.value.byHour
        .filter(h => isClubHour.value(h.hour) && h.count > 0)
        .map(h => ({
          ...h,
          label: `${h.hour}:00`,
        }))
    })

    const maxDayCount = computed(() => {
      if (!dayOfWeekData.value.length) return 1

      return Math.max(...dayOfWeekData.value.map(d => d.count), 1)
    })

    const maxHourCount = computed(() => {
      if (!filteredHourData.value.length) return 1

      return Math.max(...filteredHourData.value.map(h => h.count), 1)
    })

    const peakDay = computed(() => {
      if (!dayOfWeekData.value.length) return null
      const peak = dayOfWeekData.value.reduce((max, d) => (d.count > max.count ? d : max), dayOfWeekData.value[0])

      return peak.count > 0 ? peak : null
    })

    const peakHour = computed(() => {
      if (!filteredHourData.value.length) return null
      const peak = filteredHourData.value.reduce((max, h) => (h.count > max.count ? h : max), filteredHourData.value[0])

      return peak.count > 0 ? peak : null
    })

    const getBarWidth = (count, maxCount) => {
      if (maxCount === 0) return '0%'
      const percentage = (count / maxCount) * 80

      return `${percentage}%`
    }

    // 初回ロード
    fetchCustomerVisitPattern(props.customerId)

    return {
      visitPattern,
      isLoading,
      totalVisits,
      dayOfWeekData,
      filteredHourData,
      maxDayCount,
      maxHourCount,
      peakDay,
      peakHour,
      getBarWidth,
      icons: {
        mdiChartBar,
      },
    }
  },
}
</script>

<style scoped>
.day-label-col {
  width: 24px;
  min-width: 24px;
  text-align: center;
}

.hour-label-col {
  width: 48px;
  min-width: 48px;
  text-align: right;
  padding-right: 8px;
}

.bar {
  height: 16px;
  min-width: 2px;
  transition: width 0.3s ease;
}
</style>
